import { useEffect } from 'react'
import { PortalProvider, usePortal } from '../contexts/PortalContext'
import { SupplierPortalLayout } from './SupplierPortalLayout'
import { SupplierWelcomeScreen } from './SupplierWelcomeScreen'
import { SupplierFormView } from './SupplierFormView'
import { ConfirmationScreen } from './ConfirmationScreen'
import '../styles/supplier.css'

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function PortalSkeleton() {
  return (
    <div className="w-full max-w-lg mx-auto border rounded-xl shadow-lg p-8 space-y-6 bg-white" style={{ borderColor: 'var(--supplier-border)' }}>
      <div className="flex flex-col items-center space-y-3">
        <div className="w-14 h-14 rounded-full animate-pulse" style={{ backgroundColor: 'var(--supplier-border)' }} />
        <div className="h-5 w-40 rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-border)' }} />
      </div>
      <div className="space-y-2">
        <div className="h-7 w-3/4 mx-auto rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-border)' }} />
        <div className="h-4 w-24 mx-auto rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-accent)' }} />
      </div>
      <div className="h-4 w-full rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-accent)' }} />
      <div className="h-4 w-5/6 mx-auto rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-accent)' }} />
      <div className="h-16 w-full rounded-lg animate-pulse" style={{ backgroundColor: 'var(--supplier-accent)' }} />
      <div className="h-10 w-full rounded animate-pulse" style={{ backgroundColor: 'var(--supplier-border)' }} />
    </div>
  )
}

// ─── Page d'erreur ────────────────────────────────────────────────────────────

function PortalError({ status }: { status: number }) {
  // CA-7 & CA-8 — Messages d'erreur selon le code HTTP, aucune donnée sensible exposée
  const displayMessage = status === 401
    ? 'Ce lien a expiré ou a déjà été utilisé. Contactez votre interlocuteur pour un nouveau lien.'
    : 'Une erreur est survenue. Veuillez réessayer ou contacter votre interlocuteur.'

  return (
    <div className="w-full max-w-md mx-auto text-center space-y-4 p-8 bg-white rounded-xl border shadow-lg" style={{ borderColor: 'var(--supplier-border)' }}>
      <div
        className="inline-flex items-center justify-center w-12 h-12 rounded-full text-white text-xl font-bold"
        style={{ backgroundColor: '#DC2626' }}
      >
        !
      </div>
      <h2 className="text-lg font-semibold" style={{ color: 'var(--supplier-text)' }}>
        Accès impossible
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed">{displayMessage}</p>
    </div>
  )
}

// ─── Contenu du portail (utilise usePortal) ───────────────────────────────────

function PortalContent() {
  const { step, errorStatus, data } = usePortal()
  const orgName = data?.organization.name

  return (
    <SupplierPortalLayout organizationName={orgName}>
      {step === 'loading' && <PortalSkeleton />}
      {step === 'error' && <PortalError status={errorStatus ?? 500} />}
      {step === 'welcome' && <SupplierWelcomeScreen />}
      {step === 'submitted' && <ConfirmationScreen />}
      {step === 'form' && <SupplierFormView />}
    </SupplierPortalLayout>
  )
}

// ─── Composant principal SupplierPortal ───────────────────────────────────────

export function SupplierPortal({ token }: { token: string }) {
  // CA-1 — Appliquer le thème fournisseur sur body avec cleanup au démontage
  useEffect(() => {
    document.body.classList.add('supplier-theme')
    return () => document.body.classList.remove('supplier-theme')
  }, [])

  return (
    <PortalProvider token={token}>
      <PortalContent />
    </PortalProvider>
  )
}
